/**
 * LA SEZIONE PRATICA DELL'ARTICOLO: come si chiama, dove finisce il corpo e comincia lei, e com'è
 * fatta dentro. È l'unica fonte di verità su queste tre cose.
 *
 * NASCE DA UN PROBLEMA DI IMPAGINAZIONE, non di dati: «l'informazione è interessante, ma
 * graficamente troppo invasiva sull'articolo». La causa è misurabile e sta in globals.css (righe
 * 42-64, che sovrascrivono `.prose` a mano): dentro il corpo la scheda di servizio erede
 * `font-size: 1.125rem`, `line-height: 1.8` e `--tw-prose-bullets: var(--color-accent)`, cioè viene
 * composta con la voce del racconto — corpo grande, interlinea da lettura lunga, pallini colorati —
 * mentre è un riepilogo di dati che si consulta. Per portarla fuori dal `.prose` bisogna saperne i
 * confini nell'albero Lexical, e questo file è quel sapere.
 *
 * PERCHÉ UN MODULO NUOVO E NON contentGate.ts, che il titolo lo conosceva già. Perché contentGate.ts
 * è il gate del worker: 1150 righe di regex, blacklist e ledger. <ArticleView> è ISOMORFO — lo usa
 * la pagina SSR e lo usa <ArticleStream>, che è 'use client' — quindi qualunque modulo importato da
 * lì finisce nel bundle del browser, e spedire il gate intero a chi legge un articolo sarebbe un
 * costo pagato per una scelta tipografica. Questo file non ha NEMMENO UN IMPORT, e contentGate.ts
 * ri-importa da qui il titolo e i due normalizzatori invece di ridefinirli: così la verità sul
 * recinto resta una sola e nessuno può sdoppiarla.
 */

// ---------------------------------------------------------------------------------------------
// Normalizzazione — arrivate qui da contentGate.ts, che ora le ri-importa
// ---------------------------------------------------------------------------------------------

/**
 * Tipografia -> ASCII. NON opzionale: il post 41 conteneva `all’alba` con U+2019, quindi una
 * blacklist scritta con l'apostrofo ASCII lo mancava del tutto. Sui due post lunghi sono stati
 * contati 50 e 75 caratteri curvi contro un'istruzione ASCII esplicita nel prompt: la tipografia
 * è la prova sperimentale che l'obbedienza del modello non è una base su cui fondare un gate.
 *
 * Gemella di norm() in os-service-article-generator/src/ledger.js.
 */
export function normalizeTypography(s: string): string {
  return String(s ?? '')
    .normalize('NFKC')
    .replace(/[‘’‚‛′´`]/g, "'")
    .replace(/[“”„‟″«»]/g, '"')
    .replace(/[‐-―−­]/g, '-')
    .replace(/…/g, '...')
    .replace(/[   -​  　﻿]/g, ' ')
}

/** normalizeTypography + minuscole + accenti rimossi. Per i confronti insensibili alla forma. */
export function fold(s: string): string {
  return normalizeTypography(s)
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
}

// ---------------------------------------------------------------------------------------------
// Il titolo del recinto: le uniche due varianti che esistono in tabella
// ---------------------------------------------------------------------------------------------

/**
 * Titolo esatto della sezione finale che fa da recinto ai dati di Classe 0: lo prescrive il prompt
 * di rewrite (rewrite.ts) e lo cerca il gate G6. Sui 35 post del tenant 1 è il titolo dei post 57 e
 * 58, cioè di tutti quelli usciti dalla pipeline.
 */
export const PRACTICAL_HEADING = 'Informazioni pratiche'

/**
 * LA VARIANTE STORICA, e non è un'ipotesi difensiva: è il titolo del post 43, scritto a mano prima
 * che il prompt fissasse la formula, ed è l'articolo PUBBLICATO che ha fatto nascere questa
 * richiesta — cioè l'unico caso che un utente ha davvero visto. Censiti tutti e 35 i post del
 * tenant: di varianti ce ne sono due, questa e quella sopra, nessun'altra.
 *
 * L'accento di «proprietà» non è un rischio: il confronto passa da fold(), che toglie i diacritici.
 * Si scrive come sta in tabella e si confronta insensibile alla forma.
 */
export const PRACTICAL_HEADING_LEGACY = 'Indirizzo, proprietà e contatti'

/** Chi trovasse una terza variante la aggiunge QUI, non in un `if` dentro un componente. */
export const PRACTICAL_HEADINGS: readonly string[] = [PRACTICAL_HEADING, PRACTICAL_HEADING_LEGACY]

/**
 * L'incipit, ripiegato, della riga di accessibilità: riconoscerla serve perché è un AVVISO e non un
 * dato, e va composta come tale (è anche la parte che l'utente ha indicato come la più invadente).
 *
 * Si riconosce dal solo incipit e non dalla stringa intera perché quella stringa in pagina esiste in
 * DUE forme, misurate: nel post 58 la voce è un unico `text` senza etichetta che contiene ACCESS_LINE
 * verbatim (rewrite.ts prescrive `<li>${ACCESS_LINE}</li>`, senza etichetta, perché la formula i due
 * punti li contiene già); nel post 43 l'etichetta «Accessibilità» è in grassetto e il valore comincia
 * con i due punti. Ripiegate, le due forme cominciano identiche. L'accento non conta: lo toglie fold().
 *
 * La formulazione canonica dell'avviso resta ACCESS_LINE in contentGate.ts, che è la sua casa (G6A la
 * usa come whitelist letterale): qui non si duplica il testo, si riconosce il suo attacco.
 */
const ACCESS_NOTICE_PREFIX = 'accessibilita'

/**
 * L'incipit, ripiegato, della formula di ASSENZA — la riga che dice che un dato non risulta.
 *
 * Serve per la stessa ragione dell'avviso qui sopra, applicata al caso opposto: una voce che dichiara
 * di non sapere non è un dato e non va composta come un dato. Sul post 43 — l'unico PUBBLICATO che
 * abbia questa sezione, cioè esattamente il caso che ha aperto la richiesta «graficamente troppo
 * invasiva» — due voci su sei sono queste:
 *   «Orari: non risultano informazioni ufficiali aggiornate; verificare prima di partire.»
 *   «Biglietto: non risultano informazioni ufficiali aggiornate; verificare prima di partire.»
 * Stampate col peso delle altre, un terzo del riquadro è occupato da righe che non dicono niente.
 *
 * NON SI CANCELLANO, e la distinzione è il punto: sono testo pubblicato, e un riquadro che nasconde a
 * render time una riga dell'articolo è un riquadro di cui non ci si può fidare (fra sei mesi nessuno
 * ricorda perché quella riga non compare). Si compongono in corpo minore, come l'avviso: restano
 * leggibili, smettono di competere coi dati veri.
 *
 * Vale solo per il contenuto storico: dal luglio 2026 il prompt di rewrite vieta di scrivere le
 * mancanze in pagina, quindi la pipeline non ne produce più. La formulazione canonica resta ABSENCE
 * in contentGate.ts, che è la sua casa (G6 la usa come whitelist letterale dentro il recinto): qui
 * non si duplica il testo, si riconosce il suo attacco.
 */
const ABSENCE_VALUE_PREFIX = 'non risultano informazioni ufficiali aggiornate'

// ---------------------------------------------------------------------------------------------
// La forma minima dell'albero Lexical
// ---------------------------------------------------------------------------------------------

/**
 * Un nodo Lexical visto da fuori. Payload lo tipizza `{ type: any; version: number; [k: string]:
 * unknown }` (payload-types.ts:453), cioè strutturalmente aperto: ridichiararlo qui con le sole
 * chiavi che questo modulo legge lo rende assegnabile da `post.content.root.children` senza cast, e
 * rende le fixture del test scrivibili a mano senza dover inventare un `version` su ogni nodo.
 */
export type LexicalNode = {
  type?: unknown
  /** Solo sugli heading: 'h2', 'h3', … */
  tag?: unknown
  /** Solo sui nodi `text`. */
  text?: unknown
  /** Sui nodi `text` è la bitmask dei formati inline (1 = grassetto, 2 = corsivo). */
  format?: unknown
  children?: unknown
  [k: string]: unknown
}

/** I figli di un nodo, sempre un array: l'albero arriva dal database e non si dà per buono. */
export function childNodes(node: LexicalNode | undefined | null): LexicalNode[] {
  return Array.isArray(node?.children) ? (node.children as LexicalNode[]) : []
}

/**
 * Il testo di un nodo e dei suoi discendenti, CONCATENATO SENZA SEPARATORE.
 *
 * Senza separatore perché i nodi `text` inline sono pezzi della stessa stringa, non blocchi: nel
 * post 58 la voce «Come arrivare:» + « 1,2 KM…» è etichetta e valore già separati dallo spazio che
 * il secondo nodo si porta dietro. Unirli con uno spazio in più falserebbe i confronti (fold()
 * collassa gli spazi, ma non quelli che non ci sono).
 */
export function nodeText(node: LexicalNode | undefined | null): string {
  if (!node) return ''
  if (typeof node.text === 'string') return node.text
  return childNodes(node).map(nodeText).join('')
}

// ---------------------------------------------------------------------------------------------
// Lo split: dove finisce il corpo
// ---------------------------------------------------------------------------------------------

export type PracticalSplit = {
  /** I figli della root che restano nel corpo, da dare a <RichText> dentro `.prose`. */
  body: LexicalNode[]
  /** La sezione pratica, heading incluso e in prima posizione. `null` se l'articolo non ne ha. */
  practical: LexicalNode[] | null
}

/** L'heading che apre il recinto: h2, e il titolo è una delle due varianti censite. */
function isPracticalHeading(node: LexicalNode | undefined): boolean {
  if (!node || node.type !== 'heading' || node.tag !== 'h2') return false
  const title = fold(nodeText(node))
  return PRACTICAL_HEADINGS.some((h) => title.startsWith(fold(h)))
}

/**
 * Spezza i figli della root in [corpo] + [sezione pratica].
 *
 * L'ANCORA È L'HEADING, MAI LA LISTA, ed è il punto in cui questa funzione si distingue da una
 * regressione. Nel tenant 1 ci sono 13 nodi `list ul bullet` e solo 3 sono la sezione pratica: 7
 * sono le SCHEDE DATI per attrazione dentro le sezioni tematiche dei post 40 e 41, di forma quasi
 * identica — etichette in grassetto Orari/Contatti/Biglietto/Accessibilità e perfino la stessa riga
 * di accessibilità. Il discriminante, verificato su tutti e 13: la lista della sezione pratica è
 * immediatamente preceduta da un `heading`, le schede dati da un `paragraph`. Ancorandosi
 * all'heading col titolo giusto, le schede dati non diventano nemmeno candidate.
 *
 * E NON SI GUARDANO LE PAROLE DEL TITOLO. Un'euristica lessicale su «orari», «costi»,
 * «accessibilità» produce 4 falsi positivi su titoli tematici legittimi già pubblicati: «Restauro,
 * fruizione sostenibile e orari», «Organizzare la visita e orari di apertura», «Economia, costi e
 * accessibilità», «Ruolo sociale, istituzionale e orari». Si confrontano le due varianti esatte, con
 * `startsWith` come fa già splitPracticalSection() sull'HTML in contentGate.ts.
 *
 * DOVE LE DUE FUNZIONI NON COINCIDONO, e va detto invece di lasciarlo credere: il gate confronta
 * SOLO `PRACTICAL_HEADING`, non `PRACTICAL_HEADINGS`. Misurato chiamandolo: su un HTML col titolo
 * storico `splitPracticalSection()` torna `practical: ''`, cioè per il gate il post 43 non ha
 * recinto affatto, mentre qui la sezione si riconosce. Sul contenuto della pipeline le due
 * coincidono (è l'unico titolo che il prompt genera) e il post 43 è editoriale, quindi il gate su di
 * lui non è mai girato; ma chi allineasse i due lati deve passare da `PRACTICAL_HEADINGS` là, non
 * duplicare una terza lista.
 *
 * SI PRENDE L'ULTIMO heading che corrisponde, E TUTTO CIÒ CHE LO SEGUE fino alla fine dei children.
 * L'ultimo perché il recinto è la sezione finale, e in coda perché il post 43 dopo l'elenco ha un
 * paragrafo (la formula standard della scheda comunale sull'accessibilità) che parla della sezione,
 * non del corpo: nel riquadro sta dove appartiene concettualmente.
 *
 * SE NON TROVA NULLA, `body` è ESATTAMENTE l'array ricevuto — stesso riferimento, nessun nodo
 * ricostruito. 32 post su 35 sono in questo caso e non devono cambiare di una virgola: il chiamante
 * può testare `practical === null` e passare a <RichText> l'oggetto `content` originale.
 */
export function splitPracticalNodes(children: LexicalNode[] | null | undefined): PracticalSplit {
  const nodes = Array.isArray(children) ? children : []
  let start = -1
  for (let i = 0; i < nodes.length; i++) if (isPracticalHeading(nodes[i])) start = i
  if (start < 0) return { body: nodes, practical: null }
  return { body: nodes.slice(0, start), practical: nodes.slice(start) }
}

// ---------------------------------------------------------------------------------------------
// Dentro la sezione: etichetta, valore, avviso
// ---------------------------------------------------------------------------------------------

export type PracticalItem = {
  /** L'etichetta SENZA i due punti, o `null` se la voce non ne ha una in grassetto. */
  label: string | null
  /** Il valore come nodi inline: conserva i grassetti interni («1,2 KM», «2 euro»). */
  value: LexicalNode[]
  /** La voce è l'avviso di accessibilità: va composta come avviso, non come dato. */
  isAccessibilityNotice: boolean
  /** Il valore è la formula di assenza: la voce dichiara di non sapere, e non pesa come un dato. */
  isAbsence: boolean
}

export type PracticalContent = {
  /** Il titolo della sezione, come lo scrive l'articolo (le due varianti si mostrano entrambe). */
  heading: string
  /** Le voci-dato, in ordine, senza l'avviso di accessibilità. */
  items: PracticalItem[]
  /** L'avviso di accessibilità, estratto dall'elenco perché va composto a parte. */
  notice: PracticalItem | null
  /** I blocchi di coda della sezione (nel post 43: un paragrafo dopo l'elenco). */
  trailing: LexicalNode[]
}

/**
 * Toglie dal primo nodo di testo del valore i due punti e lo spazio d'attacco, con una COPIA
 * SUPERFICIALE: l'albero arriva da `post.content` e questa funzione non deve mutarlo (nello scroll
 * infinito lo stesso oggetto post resta in stato React e verrebbe ri-renderizzato già mutilato).
 *
 * Serve perché le due convenzioni misurate mettono i due punti in due posti diversi — nel post 58
 * dentro il grassetto («Come arrivare:»), nel post 43 fuori (grassetto «Indirizzo», valore
 * «: Strada…») — e il riquadro li ricompone in un modo solo.
 */
function stripValueLeadIn(nodes: LexicalNode[]): LexicalNode[] {
  const first = nodes[0]
  if (!first || typeof first.text !== 'string') return nodes
  const text = first.text.replace(/^\s*:?\s*/, '')
  return [{ ...first, text }, ...nodes.slice(1)]
}

/**
 * Scompone una `listitem` della sezione pratica in etichetta + valore.
 *
 * L'ETICHETTA È SOLO IL PRIMO FIGLIO, e il grassetto da solo non la identifica: nei valori del post
 * 58 «1,2 KM» e «2 euro» sono nodi `text` con format 1 in mezzo al valore. Chi cercasse «il testo in
 * grassetto» troverebbe tre etichette in una voce che ne ha una.
 *
 * E DEVE FINIRE CON I DUE PUNTI, da una parte o dall'altra: il grassetto li include (post 58) oppure
 * il valore li apre (post 43). Se non ci sono né qua né là, quel grassetto non è un'etichetta ma una
 * frase enfatizzata, e la voce si compone intera — altrimenti il riquadro stamperebbe due punti che
 * l'articolo non ha scritto, cioè inventerebbe una struttura.
 */
export function practicalItem(listItem: LexicalNode): PracticalItem {
  const kids = childNodes(listItem)
  const first = kids[0]
  const firstText = typeof first?.text === 'string' ? first.text : ''
  const isBold = first?.type === 'text' && ((Number(first.format) || 0) & 1) === 1
  const hasLabel = isBold && Boolean(firstText.trim()) && (/:\s*$/.test(firstText) || /^\s*:/.test(nodeText(kids[1])))

  const rest = hasLabel ? kids.slice(1) : kids
  const value = hasLabel ? stripValueLeadIn(rest) : rest
  return {
    label: hasLabel ? firstText.replace(/\s*:\s*$/, '').trim() : null,
    value,
    // Sul TESTO INTERO della voce, così l'avviso si riconosce in entrambe le sue forme: con
    // l'etichetta in grassetto (post 43) e senza (post 58, dove la formula la contiene già).
    isAccessibilityNotice: fold(nodeText(listItem)).startsWith(ACCESS_NOTICE_PREFIX),
    // Sul VALORE e non sulla voce intera, al contrario dell'avviso: la formula di assenza sta SEMPRE
    // dopo un'etichetta («Orari:», «Biglietto:»), quindi il testo della voce non comincia con lei.
    isAbsence: fold(value.map(nodeText).join('')).startsWith(ABSENCE_VALUE_PREFIX),
  }
}

/**
 * Scompone la sezione pratica in ciò che il riquadro deve impaginare. Sta qui e non nel componente
 * perché è logica pura e si testa senza montare React.
 *
 * `practical[0]` è l'heading per costruzione di splitPracticalNodes(), ma non si dà per buono: se
 * per qualunque ragione non lo fosse, il titolo ripiega sulla formula canonica invece di stampare il
 * testo del primo blocco qualunque esso sia.
 *
 * NIENTE SPARISCE: le voci vengono da OGNI nodo `list` della sezione, e qualunque altro blocco (il
 * paragrafo di coda del post 43, o un blocco che oggi non prevediamo) finisce in `trailing`. Un
 * riquadro che perde silenziosamente una riga dell'articolo è peggio di un riquadro brutto.
 */
export function practicalContent(nodes: LexicalNode[]): PracticalContent {
  const [head, ...blocks] = Array.isArray(nodes) ? nodes : []
  const heading = head?.type === 'heading' ? nodeText(head).trim() : ''

  const items: PracticalItem[] = []
  const trailing: LexicalNode[] = []
  let notice: PracticalItem | null = null

  for (const block of head?.type === 'heading' ? blocks : nodes) {
    if (block?.type === 'list') {
      for (const li of childNodes(block)) {
        const item = practicalItem(li)
        // Il primo avviso vince: se un articolo ne portasse due, il secondo resta una voce-dato
        // invece di sparire dal riquadro.
        if (item.isAccessibilityNotice && !notice) notice = item
        else items.push(item)
      }
    } else if (block) {
      trailing.push(block)
    }
  }

  return { heading: heading || PRACTICAL_HEADING, items, notice, trailing }
}
